import React from "react";
import {
  Send,
  Bell,
  Trash2,
  Mail,
  Plus,
  FileSignature,
  ClipboardList,
} from "lucide-react";
import Button from "../../common/ui/button/Button";
import Badge from "../../common/ui/badge/Badge";
import { Table, TableColumn } from "../../common/tables/Table";
import { DateCell, ActionsCell } from "../../common/tables/cells";
import {
  RequestParticipant,
  ParticipantStatus,
  ParticipantRole,
} from "@secure-caseworker/shared";

type ParticipantsTabProps = {
  participants: RequestParticipant[];
  canEdit: boolean;
  onResendInvitation: (participantId: string) => void;
  onEditReminders: (participant: RequestParticipant) => void;
  onRemoveParticipant: (participantId: string) => void;
  onAddParticipant: () => void;
};

const getParticipantStatusBadge = (status: ParticipantStatus) => {
  switch (status) {
    case ParticipantStatus.PENDING:
      return <Badge color="warning">Pending</Badge>;
    case ParticipantStatus.COMPLETED:
      return <Badge color="success">Completed</Badge>;
    case ParticipantStatus.DECLINED:
      return <Badge color="error">Declined</Badge>;
    case ParticipantStatus.FAILED:
      return <Badge color="error">Failed</Badge>;
    case ParticipantStatus.REPLACED:
      return <Badge color="light">Replaced</Badge>;
    default:
      return <Badge color="light">{status}</Badge>;
  }
};

const getRoleBadge = (participant: RequestParticipant) => {
  if (participant.role === ParticipantRole.SIGNER) {
    const signerLabel = participant.template_signer?.label || "Signer";
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
        <FileSignature size={12} />
        {signerLabel}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
      <ClipboardList size={12} />
      Responder
    </span>
  );
};

export const ParticipantsTab: React.FC<ParticipantsTabProps> = ({
  participants,
  canEdit,
  onResendInvitation,
  onEditReminders,
  onRemoveParticipant,
  onAddParticipant,
}) => {
  const participantColumns: TableColumn<RequestParticipant>[] = [
    {
      header: "Name / Email",
      accessor: "email",
      render: (_value, row) => (
        <div>
          <p className="font-medium text-black dark:text-white">
            {row.name || "No name"}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <Mail size={12} />
            {row.email}
          </p>
        </div>
      ),
    },
    {
      header: "Role",
      accessor: "role",
      render: (_value, row) => getRoleBadge(row),
    },
    {
      header: "Status",
      accessor: "status",
      render: (value) => getParticipantStatusBadge(value),
    },
    {
      header: "Completed",
      accessor: "completed_at",
      render: (value) => (
        <DateCell date={value || ""} format="datetime" fallback="-" />
      ),
    },
    {
      header: "Actions",
      accessor: "id",
      render: (_value, row) => (
        <ActionsCell
          items={[
            ...(row.status === ParticipantStatus.PENDING && canEdit
              ? [
                  {
                    label: "Resend Invitation",
                    icon: <Send size={14} />,
                    onClick: () => onResendInvitation(row.id),
                  },
                  {
                    label: "Edit Reminders",
                    icon: <Bell size={14} />,
                    onClick: () => onEditReminders(row),
                  },
                ]
              : []),
            ...(canEdit && row.status !== ParticipantStatus.COMPLETED
              ? [
                  {
                    label: "",
                    separator: true,
                    onClick: () => {},
                  },
                  {
                    label: "Remove",
                    icon: <Trash2 size={14} />,
                    onClick: () => onRemoveParticipant(row.id),
                    danger: true,
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {participants && participants.length > 0 ? (
        <Table columns={participantColumns} data={participants} />
      ) : (
        <div className="text-center py-8 text-gray-500">
          No participants assigned to this request
        </div>
      )}
      <div className="flex flex-row-reverse">
        {canEdit && (
          <Button variant="outline" size="sm" onClick={onAddParticipant}>
            <Plus size={16} className="mr-1" />
            Add Participant
          </Button>
        )}
      </div>
    </div>
  );
};
